import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Box, Button, Typography, TextField } from "@mui/material";
import { styled } from "@mui/system";
import Api from "../../utils/Api";

// Enhanced styled components
const CategoryFormContainer = styled(Box)(({ theme }) => ({
    color: "#fff",
    maxWidth: 800,
    margin: "40px auto",
    padding: "40px",
    borderRadius: "20px",
    backgroundImage: "linear-gradient(135deg, #1C1C1C 0%, #333333 100%)",
    boxShadow: "0px 15px 30px rgba(0, 0, 0, 0.4), 0px 10px 15px rgba(0, 0, 0, 0.2)",
}));

const CategoryField = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    marginBottom: "30px",
}));

const Label = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    color: "#0BD349",
    marginBottom: "10px",
    fontSize: "18px",
    letterSpacing: "0.5px",
}));

const StyledTextField = styled(TextField)({
    "& .MuiInputBase-input": {
        color: "#fff",
    },
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#444",
        "& fieldset": {
            borderColor: "#0BD349",
        },
        "&:hover fieldset": {
            borderColor: "#0BD349",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0BD349",
        },
    },
    "& label.Mui-focused": {
        color: "#0BD349",
    },
});

const StyledButton = styled(Button)(({ theme }) => ({
    padding: "12px 24px",
    fontSize: "16px",
    borderRadius: "50px",
    textTransform: "none",
    boxShadow: "0px 10px 20px rgba(11, 211, 73, 0.3)",
    transition: "background-color 0.3s, color 0.3s, transform 0.3s",
    "&:hover": {
        backgroundColor: "#0BD349",
        color: "#000",
        transform: "translateY(-3px)",
    },
    "&:disabled": {
        backgroundColor: "#666",
        color: "#999",
    },
}));

function CategoryForm({ existingCategory = null, isNew = true }) {
    const [categoryName, setCategoryName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Set category name for editing
        if (existingCategory) {
            setCategoryName(existingCategory.category_name);
        }
    }, [existingCategory]);

    const handleInputChange = (e) => {
        setCategoryName(e.target.value);
    };

    const handleCancel = useCallback(() => {
        navigate("/dashboard/categories");
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (!categoryName.trim()) {
                throw new Error("Category name is required");
            }

            const payload = {
                category_name: categoryName.trim(),
            };

            if (isNew) {
                // Create new category
                const response = await Api.post("/add-category", payload);
                if (response.data.success) {
                    Swal.fire({
                        icon: "success",
                        title: "Category Added",
                        text: response.data.message,
                        iconColor: '#000',
                        confirmButtonColor: '#000'
                    });
                } else {
                    throw new Error(response.data.message || "Failed to create category");
                }
            } else {
                // Update existing category
                const response = await Api.put(`/update-category/${existingCategory.id}`, payload);
                if (response.data.success) {
                    Swal.fire({
                        icon: "success",
                        title: "Category Updated",
                        text: response.data.message,
                    });
                } else {
                    throw new Error(response.data.message || "Failed to update category");
                }
            }

            navigate("/dashboard/categories");
        } catch (error) {
            console.error("Error handling form submission:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "Something went wrong",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <CategoryFormContainer>
            <Typography
                variant="h4"
                gutterBottom
                style={{
                    color: "#0BD349",
                    textAlign: "center",
                    marginBottom: "20px",
                    fontWeight: "700",
                    letterSpacing: "1px",
                }}
            >
                {isNew ? "Neue Kategorie erstellen" : "Kategorie bearbeiten"}
            </Typography>
            <form onSubmit={handleSubmit}>
                <CategoryField>
                    <Label>Kategoriename:</Label>
                    <StyledTextField
                        name="category_name"
                        value={categoryName}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                    />
                </CategoryField>
                <Box display="flex" justifyContent="center" gap="20px">
                    <StyledButton
                        type="button"
                        variant="contained"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: "#fff",
                            color: "#000",
                        }}
                    >
                        {/* Cancel */}
                        Abbrechen
                    </StyledButton>

                    <StyledButton
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: isSubmitting ? "#666" : "#0BD349",
                            color: "#fff",
                        }}
                    >
                        {isSubmitting ? "Submitting..." : isNew ? "Kategorie erstellen" : "Kategorie aktualisieren"}
                    </StyledButton>
                </Box>
            </form>
        </CategoryFormContainer>
    );
}

export default CategoryForm;
